import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const brandMidnight = '#1F2433';
const brandPrimary = '#2F3A72';
const brandPrimaryLight = '#5161A4';
const brandPrimaryDark = '#252E55';
const brandAccent = '#F6785F';
const brandAccentLight = '#FF9C82';
const brandAccentDark = '#E6624A';
const brandMuted = '#5B6270';
const brandBorder = '#E4E8F1';
const brandBackground = '#F5F7FB';

let baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brandPrimary,
      light: brandPrimaryLight,
      dark: brandPrimaryDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: brandAccent,
      light: brandAccentLight,
      dark: brandAccentDark,
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#2EBD85',
    },
    warning: {
      main: '#FFB648',
    },
    error: {
      main: '#F36960',
    },
    info: {
      main: '#4C7CF2',
    },
    text: {
      primary: brandMidnight,
      secondary: brandMuted,
    },
    divider: brandBorder,
    background: {
      default: brandBackground,
      paper: '#FFFFFF',
    },
    body: {
      main: brandMuted,
    },
    white: {
      main: '#FFFFFF',
    },
    green: {
      main: '#2EBD85',
    },
    custom: {
      light: '#FFFFFF',
      main: brandAccent,
      dark: brandAccentDark,
      contrastText: brandMidnight,
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  shape: {
    borderRadius: 16,
  },
  spacing: 8,
  typography: {
    htmlFontSize: 10,
    fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h1: {
      fontSize: '4.8rem',
      fontWeight: 600,
      lineHeight: 1.12,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '3.6rem',
      fontWeight: 600,
      lineHeight: 1.18,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '2.8rem',
      fontWeight: 600,
      lineHeight: 1.22,
    },
    h4: {
      fontSize: '2.2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4_v2: {
      color: '#FFFFFF',
      fontSize: '2.4rem',
      fontWeight: 500,
      lineHeight: 1.32,
    },
    h5: {
      color: brandMidnight,
      fontSize: '1.8rem',
      fontWeight: 600,
      lineHeight: 1.35,
    },
    h6: {
      color: brandMidnight,
      fontSize: '1.6rem',
      fontWeight: 600,
      lineHeight: 1.35,
    },
    subtitle1: {
      fontSize: '1.6rem',
      fontWeight: 500,
      color: brandMuted,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '1.4rem',
      fontWeight: 500,
      color: brandMuted,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1.6rem',
      fontWeight: 400,
      color: brandMuted,
      lineHeight: 1.65,
    },
    body2: {
      fontSize: '1.4rem',
      fontWeight: 400,
      color: brandMuted,
      lineHeight: 1.6,
    },
    button: {
      fontSize: '1.6rem',
      fontWeight: 600,
      textTransform: 'none',
    },
    cartInfo: {
      fontSize: '1.6rem',
      fontWeight: 400,
      lineHeight: 1.45,
      color: brandMidnight,
    },
    s16_w600: {
      fontSize: '1.6rem',
      fontWeight: 600,
      lineHeight: 1.55,
      color: brandMidnight,
    },
    s16_w500: {
      fontSize: '1.6rem',
      fontWeight: 500,
      lineHeight: 1.55,
      color: brandMuted,
    },
    s16_w500_orange: {
      fontSize: '1.6rem',
      fontWeight: 500,
      lineHeight: 1.55,
      color: brandAccent,
    },
    s24_w600: {
      fontSize: '2.4rem',
      fontWeight: 600,
      lineHeight: 1.35,
      color: brandMidnight,
    },
    bodySmallCaps: {
      fontSize: '1.2rem',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: brandMuted,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        body: {
          backgroundColor: brandBackground,
          color: brandMidnight,
        },
        '::selection': {
          backgroundColor: brandPrimary,
          color: '#FFFFFF',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
          padding: '0.85rem 1.9rem',
          boxShadow: '0px 10px 24px rgba(47, 58, 114, 0.16)',
          transition: 'transform 120ms ease, box-shadow 200ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 12px 26px rgba(47, 58, 114, 0.22)',
          },
          '&.Mui-disabled': {
            background: '#D0D6E6',
            color: '#FFFFFF',
            boxShadow: 'none',
          },
        },
        containedSecondary: {
          boxShadow: '0px 10px 24px rgba(246, 120, 95, 0.18)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow:
            '0px 24px 60px rgba(31, 36, 51, 0.08), 0px 8px 24px rgba(31, 36, 51, 0.04)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow:
            '0px 24px 60px rgba(31, 36, 51, 0.08), 0px 8px 24px rgba(31, 36, 51, 0.04)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 500,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 4,
          borderRadius: 4,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1.5rem',
          minHeight: 'auto',
          padding: '1rem 1.4rem',
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontSize: '1.1rem',
          fontWeight: 600,
          color: brandMuted,
          letterSpacing: '0.12em',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: brandBorder,
        },
      },
    },
  },
});

export const theme = responsiveFontSizes(baseTheme);
